import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router';
import { Desktop, Mobile } from '../../App';
import { GoToMyTeam, formatVP } from '../../helpers/helperFunctions';
import { CaptionText, H4, H5, P1, P3 } from '../../styles/FontStyles';
import { Box, SectionSeparator } from '../../styles/GlobalComponents';
import { colors } from '../../styles/GlobalStyles';
import {
  Icon,
  IconDashboardDirects,
  IconDashboardDownline,
  IconDashboardVP
} from '../../styles/Icons';
import { Flex, FlexCenter } from '../../styles/stylesStyled';
import { DashboardInformation } from './Dashboard.types';
import { EarningsSmall, IconTitleStyled, Statistical } from './DashboardStyled';

interface StatisticalComponentProps {
  backOffice: DashboardInformation | null;
}

export const StatisticalComponent = ({
  backOffice
}: StatisticalComponentProps) => {
  const { t } = useTranslation('dashboard');
  const statisticalData = [
    {
      icon: IconDashboardVP,
      title: t('statistic.business_points'),
      left: backOffice?.user?.leftLegTotalVolume ?? '0.00',
      right: backOffice?.user?.rightLegTotalVolume ?? '0.00',
      plusLeft: backOffice?.user?.leftLegTodayVolume ?? '0.00',
      plusRight: backOffice?.user?.rightLegTodayVolume ?? '0.00',
      linkTo: 'volume'
    },
    {
      icon: IconDashboardDirects,
      title: t('statistic.directs'),
      left: backOffice?.user?.leftLegInfo?.directUserCount ?? 0,
      right: backOffice?.user?.rightLegInfo?.directUserCount ?? 0,
      plusLeft: backOffice?.user?.leftLegInfo?.todayDirectUserCount ?? 0,
      plusRight: backOffice?.user?.rightLegInfo?.todayDirectUserCount ?? 0,
      linkTo: 'direct'
    },
    {
      icon: IconDashboardDownline,
      title: t('statistic.team_downline'),
      left: backOffice?.user?.leftLegInfo?.userCount ?? 0,
      right: backOffice?.user?.rightLegInfo?.userCount ?? 0,
      plusLeft: backOffice?.user?.leftLegInfo?.todayUserCount ?? 0,
      plusRight: backOffice?.user?.rightLegInfo?.todayUserCount ?? 0,
      linkTo: 'structure'
    }
  ];

  const history = useHistory();

  return (
    <>
      <>
        <Desktop>
          <IconTitleStyled>{t('statistic.title')}</IconTitleStyled>
          <Statistical>
            {statisticalData.map((data, i) => (
              <Box p='40px' key={i}>
                <div onClick={() => GoToMyTeam(history, data.linkTo)}>
                  <FlexCenter>
                    <Icon color={colors.secondary100} mr='12' fill>
                      <data.icon />
                    </Icon>

                    <H4 color={colors.neutral900}>{data.title}</H4>
                  </FlexCenter>

                  <SectionSeparator />
                  <CaptionText caps color={colors.neutral500} semiBold>
                    {data.title === t('statistic.customers')
                      ? t('statistic.directs')
                      : t('statistic.left_team')}
                  </CaptionText>
                  <Flex
                    style={{
                      marginBottom: 32,
                      alignItems: 'end',
                      flexWrap: 'wrap'
                    }}>
                    <P1 bold mr='8'>
                      {formatVP(Number(data.left))}{' '}
                    </P1>

                    <CaptionText mb='2' flex color={colors.neutral500}>
                      <CaptionText mr='5' color={colors.secondary100} bold>
                        +{formatVP(Number(data.plusLeft))}
                      </CaptionText>{' '}
                      {t('statistic.today')}
                    </CaptionText>
                  </Flex>
                  <CaptionText caps color={colors.neutral500} semiBold>
                    {data.title === t('statistic.customers')
                      ? t('statistic.indirects')
                      : t('statistic.right_team')}
                  </CaptionText>
                  <Flex style={{ alignItems: 'end', flexWrap: 'wrap' }}>
                    <P1 bold mr='8'>
                      {formatVP(Number(data.right))}{' '}
                    </P1>

                    <CaptionText mb='2' flex color={colors.neutral500}>
                      <CaptionText mr='5' color={colors.secondary100} bold>
                        +{formatVP(Number(data.plusRight))}
                      </CaptionText>{' '}
                      {t('statistic.today')}
                    </CaptionText>
                  </Flex>
                </div>
              </Box>
            ))}
          </Statistical>
        </Desktop>

        <Mobile>
          {statisticalData.map((data, i) => (
            <div key={i} onClick={() => GoToMyTeam(history, data.linkTo)}>
              <FlexCenter>
                <Icon color={colors.secondary100} mr='12' fill>
                  <data.icon />
                </Icon>

                <H5 color={colors.neutral500}>{data.title}</H5>
              </FlexCenter>
              <EarningsSmall>
                <Box>
                  <CaptionText mb='4' color={colors.neutral500}>
                    {data.title === t('statistic.customers')
                      ? t('statistic.directs')
                      : t('statistic.left_team')}
                  </CaptionText>
                  <div>
                    <P3 bold mb='4'>
                      {formatVP(Number(data.left))}{' '}
                    </P3>

                    <CaptionText flex color={colors.neutral500}>
                      <CaptionText color={colors.secondary100} mr='5' bold>
                        +{formatVP(Number(data.plusLeft))}
                      </CaptionText>
                      {t('statistic.today')}
                    </CaptionText>
                  </div>
                </Box>
                <Box>
                  <CaptionText mb='4' color={colors.neutral500}>
                    {data.title === t('statistic.customers')
                      ? t('statistic.indirects')
                      : t('statistic.right_team')}
                  </CaptionText>

                  <div>
                    <P3 bold mb='4'>
                      {formatVP(Number(data.right))}
                    </P3>

                    <CaptionText flex color={colors.neutral500}>
                      <CaptionText color={colors.secondary100} bold mr='5'>
                        +{formatVP(Number(data.plusRight))}
                      </CaptionText>
                      {t('statistic.today')}
                    </CaptionText>
                  </div>
                </Box>
              </EarningsSmall>
            </div>
          ))}
        </Mobile>
      </>
    </>
  );
};
